import React from 'react';
import { User, Loader2, Check, X } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

interface UsernameInputProps {
  username: string;
  onChange: (value: string) => void;
  isChecking: boolean;
  isAvailable: boolean | null;
  error?: string | null;
}

export function UsernameInput({
  username,
  onChange,
  isChecking,
  isAvailable,
  error
}: UsernameInputProps) {
  const { t } = useTranslation();
  
  const getStatusColor = () => {
    if (!username || username.length < 3) return 'text-gray-400';
    if (isChecking) return 'text-gray-400';
    if (isAvailable) return 'text-green-500';
    return 'text-red-500';
  };

  const getStatusMessage = () => {
    if (!username) return null;
    if (username.length < 3) return t('profile.username.min_length');
    if (isChecking) return t('profile.username.checking');
    if (isAvailable) return t('profile.username.available');
    if (isAvailable === false) return t('profile.username.already_taken');
    return null;
  };

  const getStatusIcon = () => {
    if (!username || username.length < 3 || isChecking) {
      return null;
    }
    if (isAvailable) {
      return <Check className="w-4 h-4 text-green-500" />;
    }
    return <X className="w-4 h-4 text-red-500" />;
  };

  return (
    <div>
      <label htmlFor="username" className="block text-sm font-medium text-gray-700">
        {t('profile.username.label')}
      </label>
      <div className="mt-1 relative">
        <input
          id="username"
          type="text"
          value={username}
          onChange={(e) => {
            const value = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
            onChange(value);
          }}
          className={`pl-10 pr-10 block w-full rounded-md shadow-sm text-sm
            ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 
              'border-gray-300 focus:border-primary-500 focus:ring-primary-500'}
          `}
          placeholder={t('profile.username.placeholder')}
          required
          minLength={3}
          maxLength={30}
          pattern="^[a-z0-9][a-z0-9_-]{1,28}[a-z0-9]$"
          title={t('profile.username.validation_pattern')}
        />
        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
          {isChecking ? (
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          ) : (
            getStatusIcon()
          )}
        </div>
      </div>
      
      {(username || error) && (
        <div className="mt-1 flex items-center text-sm">
          {error ? (
            <span className="text-red-500">{error}</span>
          ) : (
            <span className={getStatusColor()}>{getStatusMessage()}</span>
          )}
        </div>
      )}
    </div>
  );
}